import {
  createContext,
  ReactNode,
  ReactNodeArray,
  useContext,
  useMemo,
  useState,
} from "react"
import * as locales from "date-fns/locale"

import { Locale } from "date-fns"
import { _numberToWords } from "./numberToWords"
import { fetchTranslations } from "api/i18n"
import { useQuery } from "react-query"

export type LangType = "EN"

export type I18nContextType = {
  lang?: LangType
  t: (key: string) => string
  locale: string
  dateLocale: Locale
  numberToWords: (_: number) => string
}

type I18nState = I18nContextType

const DEFAULT_LANG: LangType = "EN"

const DEFAULT_I18N: I18nState = {
  lang: DEFAULT_LANG,
  t: (x) => x,
  locale: "enGB",
  dateLocale: locales["enGB"],
  numberToWords: _numberToWords(),
}

export type I18nProviderProps = {
  children: ReactNode | ReactNodeArray
}

export const I18nContext = createContext<I18nContextType>(DEFAULT_I18N)

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [i18n, setI18n] = useState<I18nState>(DEFAULT_I18N)

  useQuery("translations", fetchTranslations, {
    onSuccess: (data: any) => {
      const localeParts = navigator?.language?.split("-")
      const localeJoined = localeParts.join("")
      const locale =
        locales[localeJoined as keyof typeof locales] ||
        locales[localeParts[0] as keyof typeof locales] ||
        locales["enGB"]

      setI18n((s) => ({
        lang: DEFAULT_LANG,
        t: (key: string) => data[key] ?? key,
        locale: locale?.code || s.locale,
        dateLocale: locale || s.dateLocale,
        numberToWords: _numberToWords((key: string) => data[key] ?? key),
      }))
    },
  })

  const contextValue = useMemo(() => {
    return { ...i18n }
  }, [i18n])

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  )
}

export function useI18n() {
  const i18n = useContext(I18nContext)
  return i18n
}

type TProps = {
  label: string
}
export function T(props: TProps) {
  const { label } = props
  const { t } = useI18n()
  return <span data-translation-key={label}>{t(label)}</span>
}
